import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Lock, Loader2, CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { readJsonOnce } from '../utils/safeFetch';
import { formatApiError } from '../utils/formatApiError';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MIN_LENGTH = 8;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [checking, setChecking] = useState(true);
  const [tokenError, setTokenError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenError('This reset link is missing its token. Please request a new one.');
      setChecking(false);
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/auth/reset-password/validate?token=${encodeURIComponent(token)}`);
        const data = await readJsonOnce(r);
        if (!r.ok) throw new Error(formatApiError(data && data.detail));
        setEmail((data && data.email) || '');
      } catch (err) {
        setTokenError(err.message || 'This reset link is invalid or has expired.');
      } finally {
        setChecking(false);
      }
    })();
  }, [token]);

  const validate = () => {
    if (password.length < MIN_LENGTH) return `Password must be at least ${MIN_LENGTH} characters.`;
    if (password !== confirm) return 'Passwords do not match.';
    return '';
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setError('');
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await readJsonOnce(r);
      if (!r.ok) throw new Error(formatApiError(data && data.detail));
      setDone(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < MIN_LENGTH;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md relative z-10">
        <Button variant="ghost" className="mb-6 text-muted-foreground hover:text-foreground" onClick={() => navigate('/auth')} data-testid="reset-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to sign in
        </Button>
        <div className="flex items-center mb-8">
          <img src="/ludy-wordmark.png" alt="Ludy" className="h-8" />
        </div>

        <Card className="bg-card border-border/50">
          {checking && (
            <CardContent className="py-12 text-center">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
              <p className="text-sm text-muted-foreground mt-3">Checking your reset link…</p>
            </CardContent>
          )}

          {!checking && tokenError && (
            <CardContent className="pt-8 pb-8 text-center space-y-4" data-testid="reset-token-error">
              <XCircle className="w-12 h-12 mx-auto text-destructive" />
              <h1 className="font-heading text-2xl font-semibold text-foreground">Link not valid</h1>
              <p className="text-sm text-muted-foreground">{tokenError}</p>
              <Button className="mt-2" onClick={() => navigate('/forgot-password')} data-testid="reset-request-new-btn">
                Request a new link
              </Button>
            </CardContent>
          )}

          {!checking && !tokenError && done && (
            <CardContent className="pt-8 pb-8 text-center space-y-4" data-testid="reset-success">
              <CheckCircle2 className="w-12 h-12 mx-auto text-primary" />
              <h1 className="font-heading text-2xl font-semibold text-foreground">Password updated</h1>
              <p className="text-sm text-muted-foreground">Your password has been reset. You've been signed out of all other devices. Sign in with your new password.</p>
              <Button className="mt-2" onClick={() => navigate('/auth')} data-testid="reset-go-login-btn">
                Go to sign in
              </Button>
            </CardContent>
          )}

          {!checking && !tokenError && !done && (
            <>
              <CardHeader>
                <CardTitle className="font-heading text-2xl">Choose a new password</CardTitle>
                <CardDescription>
                  {email ? <>Resetting the password for <span className="font-medium text-foreground">{email}</span>.</> : 'Enter and confirm your new password.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={submit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-password">New password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="reset-password"
                        type={showPw ? 'text' : 'password'}
                        autoFocus
                        placeholder={`At least ${MIN_LENGTH} characters`}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10 bg-background border-border"
                        data-testid="reset-password-input"
                      />
                      <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="reset-toggle-visibility" aria-label="Toggle password visibility">
                        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {tooShort && <p className="text-xs text-amber-600" data-testid="reset-hint-length">Minimum {MIN_LENGTH} characters.</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reset-confirm">Confirm password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="reset-confirm"
                        type={showPw ? 'text' : 'password'}
                        placeholder="Repeat your new password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        className="pl-10 bg-background border-border"
                        data-testid="reset-confirm-input"
                      />
                    </div>
                    {mismatch && <p className="text-xs text-amber-600" data-testid="reset-hint-mismatch">Passwords do not match.</p>}
                  </div>

                  {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}

                  <Button
                    type="submit"
                    disabled={loading || password.length < MIN_LENGTH || password !== confirm}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="reset-submit"
                  >
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating...</> : 'Reset password'}
                  </Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
